import { Button } from 'foundations-components/transitional/components/Button';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { useState, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AccreditationItem from './AccreditationItem';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { useMediaQuery } from '../../../../utils/Styling';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useIsOnOwnProfile } from './profileUtils';
import { Flex } from 'foundations-components/components/Flex';
import { DownCaratIcon } from 'foundations-assets/icons/components/DownCaratIcon';
import { UpCaratIcon } from 'foundations-assets/icons/components/UpCaratIcon';
import { EXPIRING_SOON_NOTICE_IN_DAYS } from '../../../../constants/certifications';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const maxItemsToRenderWhenCollapsed = 2;
const renderAccreditationGridItem = (showCertificateModal, isMobile, showBadgeShareButton) => accreditation => /*#__PURE__*/_jsx(GridItem, {
  marginBlockStart: "600",
  size: isMobile ? 12 : 6,
  children: /*#__PURE__*/_jsx(AccreditationItem, {
    accreditation: accreditation,
    showBadgeShareButton: showBadgeShareButton,
    showCertificateModal: showCertificateModal
  })
}, accreditation.credentialId);
export const AccreditationGridSection = ({
  title,
  accreditations,
  showBadgeShareButton,
  showCertificateModals,
  onTransferAwardsClick,
  showTransferButton,
  transferButtonLabel
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    isMobile
  } = useMediaQuery();
  const isOnOwnProfile = useIsOnOwnProfile();
  const sortNewestFirst = (a, b) => b.createdAt - a.createdAt;
  const activeAccreditations = useMemo(() => {
    return accreditations.filter(accreditation => accreditation.status === 'ACTIVE').sort(sortNewestFirst);
  }, [accreditations]);
  const expiringSoonAccreditations = useMemo(() => {
    return accreditations.filter(accreditation => accreditation.status === 'EXPIRING_SOON').sort(sortNewestFirst);
  }, [accreditations]);
  const activeAndExpiringSoonAccreditations = useMemo(() => {
    return [...expiringSoonAccreditations, ...activeAccreditations];
  }, [activeAccreditations, expiringSoonAccreditations]);
  const expiredAccreditations = useMemo(() => {
    return accreditations.filter(accreditation => accreditation.status === 'EXPIRED').sort(sortNewestFirst);
  }, [accreditations]);
  const activeAndExpiringSoonAccreditationsTruncated = useMemo(() => {
    return activeAndExpiringSoonAccreditations.slice(0, maxItemsToRenderWhenCollapsed);
  }, [activeAndExpiringSoonAccreditations]);
  const expiredAccreditationsTruncated = useMemo(() => {
    return expiredAccreditations.slice(0, maxItemsToRenderWhenCollapsed);
  }, [expiredAccreditations]);
  const displayExpiringSoonAccreditationsWarning = isOnOwnProfile && expiringSoonAccreditations.length > 0;
  const showExpandButton = activeAndExpiringSoonAccreditations.length > maxItemsToRenderWhenCollapsed || expiredAccreditations.length > maxItemsToRenderWhenCollapsed;
  const shouldShowTransferButton = showTransferButton && isOnOwnProfile;
  return /*#__PURE__*/_jsxs("section", {
    children: [/*#__PURE__*/_jsxs(Flex, {
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h4",
        children: title
      }), shouldShowTransferButton && /*#__PURE__*/_jsx(Button, {
        onClick: onTransferAwardsClick,
        responsive: false,
        use: "link",
        children: transferButtonLabel
      })]
    }), activeAndExpiringSoonAccreditations.length > 0 && /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "600",
      children: [/*#__PURE__*/_jsxs(Text, {
        as: "h5",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "profilePage.AcademyProfile.accreditations.active"
        }), ' ', displayExpiringSoonAccreditationsWarning && !isMobile && /*#__PURE__*/_jsx(Tooltip, {
          placement: "right",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.accreditations.expiringCertificationsWarning",
            options: {
              count: expiringSoonAccreditations.length,
              days: EXPIRING_SOON_NOTICE_IN_DAYS
            }
          }),
          children: /*#__PURE__*/_jsx("span", {
            "data-test-id": "expiring-soon-badge",
            style: {
              verticalAlign: 'text-bottom'
            },
            children: /*#__PURE__*/_jsx(Badge, {
              use: "count",
              children: expiringSoonAccreditations.length
            })
          })
        }), displayExpiringSoonAccreditationsWarning && isMobile && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("span", {
            children: "("
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.accreditations.expiringCertificationsWarningMobile",
            options: {
              count: expiringSoonAccreditations.length
            }
          }), /*#__PURE__*/_jsx("span", {
            children: ")"
          })]
        })]
      }), /*#__PURE__*/_jsx(Grid, {
        children: (isExpanded ? activeAndExpiringSoonAccreditations : activeAndExpiringSoonAccreditationsTruncated).map(renderAccreditationGridItem(showCertificateModals, isMobile, showBadgeShareButton))
      })]
    }), expiredAccreditations.length > 0 && /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "600",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "profilePage.AcademyProfile.accreditations.expired"
        })
      }), /*#__PURE__*/_jsx(Grid, {
        children: (isExpanded ? expiredAccreditations : expiredAccreditationsTruncated).map(renderAccreditationGridItem(showCertificateModals, isMobile, showBadgeShareButton))
      })]
    }), showExpandButton && /*#__PURE__*/_jsx("div", {
      style: {
        textAlign: 'center'
      },
      children: /*#__PURE__*/_jsx(Button, {
        marginBlockStart: "600",
        onClick: () => setIsExpanded(!isExpanded),
        use: "transparent",
        children: isExpanded ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.accreditations.loadLessButton"
          }), /*#__PURE__*/_jsx(UpCaratIcon, {})]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.accreditations.loadMoreButton"
          }), /*#__PURE__*/_jsx(DownCaratIcon, {})]
        })
      })
    })]
  });
};